import type { FlattenArea, PendingChanges, Tree } from './index';

export type TreeHistorySnapshot = {
	trees: PendingChanges<Tree>;
	flattenArea: PendingChanges<FlattenArea>;
	deleted: number[];
	draftFlattenPoints: number[];
	isDraftingTree: boolean;
};

type TreeHistoryState = {
	trees: PendingChanges<Tree>;
	flattenArea: PendingChanges<FlattenArea>;
	deleted: number[];
	draftFlatten: {
		points: number[];
	};
	isDraftingTree: boolean;
	history: TreeHistorySnapshot[];
	currentIndex: number;
};

const cloneTree = (tree: Tree): Tree => ({ ...tree });

const cloneFlattenArea = (area: FlattenArea): FlattenArea => ({
	...area,
	// coords: area.coords.map((pair) => [pair[0], pair[1]]),
});

const cloneRecord = <T>(
	record: Record<string, T>,
	cloneItem: (item: T) => T,
): Record<string, T> => {
	const next: Record<string, T> = {};
	for (const [key, value] of Object.entries(record)) {
		next[key] = cloneItem(value);
	}
	return next;
};

const clonePendingChanges = <T>(
	changes: PendingChanges<T>,
	cloneItem: (item: T) => T,
): PendingChanges<T> => {
	return {
		added: cloneRecord(changes.added as Record<string, T>, cloneItem) as PendingChanges<T>['added'],
		updated: cloneRecord(changes.updated as Record<string, T>, cloneItem) as PendingChanges<T>['updated'],
	};
};

const areNumberArraysEqual = (a: number[], b: number[]): boolean => {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i += 1) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};

const areCoordsEqual = (a: number[][] | undefined, b: number[][] | undefined): boolean => {
	if (a === undefined && b === undefined) return true;
	if (a === undefined || b === undefined) return false;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i += 1) {
		if (!areNumberArraysEqual(a[i], b[i])) return false;
	}
	return true;
};

const areTreesEqual = (a: Tree, b: Tree): boolean => (
	a.id === b.id &&
	a.treeHeight === b.treeHeight &&
	a.crownHeight === b.crownHeight &&
	a.trunkRadius === b.trunkRadius &&
	a.crownShape === b.crownShape &&
	a.x === b.x &&
	a.y === b.y &&
	a.crownRadius === b.crownRadius
);

const areFlattenAreasEqual = (a: FlattenArea, b: FlattenArea): boolean => (
	a.id === b.id &&
	a.height === b.height &&
	a.shape === b.shape &&
	areCoordsEqual(a.coords, b.coords)
);

const areRecordsEqual = <T>(
	a: Record<string, T>,
	b: Record<string, T>,
	isEqual: (left: T, right: T) => boolean,
): boolean => {
	const aKeys = Object.keys(a);
	const bKeys = Object.keys(b);
	if (aKeys.length !== bKeys.length) return false;
	for (const key of aKeys) {
		if (!(key in b)) return false;
		if (!isEqual(a[key], b[key])) return false;
	}
	return true;
};

const arePendingChangesEqual = <T>(
	a: PendingChanges<T>,
	b: PendingChanges<T>,
	isEqual: (left: T, right: T) => boolean,
): boolean => {
	return (
		areRecordsEqual(a.added as Record<string, T>, b.added as Record<string, T>, isEqual) &&
		areRecordsEqual(a.updated as Record<string, T>, b.updated as Record<string, T>, isEqual)
	);
};

const areSnapshotsEqual = (a: TreeHistorySnapshot, b: TreeHistorySnapshot): boolean => (
	arePendingChangesEqual(a.trees, b.trees, areTreesEqual) &&
	arePendingChangesEqual(a.flattenArea, b.flattenArea, areFlattenAreasEqual) &&
	areNumberArraysEqual(a.deleted, b.deleted) &&
	areNumberArraysEqual(a.draftFlattenPoints, b.draftFlattenPoints) &&
	a.isDraftingTree === b.isDraftingTree
);

export const createTreeHistorySnapshot = (state: TreeHistoryState): TreeHistorySnapshot => ({
	trees: clonePendingChanges(state.trees, cloneTree),
	flattenArea: clonePendingChanges(state.flattenArea, cloneFlattenArea),
	deleted: [...state.deleted],
	draftFlattenPoints: [...state.draftFlatten.points],
	isDraftingTree: state.isDraftingTree,
});

export const ensureTreeHistoryBase = (state: TreeHistoryState): void => {
	if (state.currentIndex === 0 && state.history.length === 0) {
		state.history.push(createTreeHistorySnapshot(state));
	}
};

export const pushTreeHistory = (state: TreeHistoryState): void => {
	const snapshot = createTreeHistorySnapshot(state);
	const current = state.history[state.currentIndex];
	if (current && areSnapshotsEqual(current, snapshot)) return;
	state.history = [...state.history.slice(0, state.currentIndex + 1), snapshot];
	state.currentIndex = state.history.length - 1;
};

export const applyTreeHistorySnapshot = (state: TreeHistoryState, snapshot: TreeHistorySnapshot): void => {
	state.trees = clonePendingChanges(snapshot.trees, cloneTree);
	state.flattenArea = clonePendingChanges(snapshot.flattenArea, cloneFlattenArea);
	state.deleted = [...snapshot.deleted];
	state.draftFlatten.points = [...snapshot.draftFlattenPoints];
	state.isDraftingTree = snapshot.isDraftingTree;
};
